import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CFormLabel,
  CLoadingButton,
  CModalHeader,
  CModalTitle,
  CForm,
  CRow,
  CFormFeedback,
  CFormSelect,
  CMultiSelect,
} from '@coreui/react-pro'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ToastComponent from 'src/components/common/TaostComponent'
import GradeService from 'src/service/GradeService'

const sameIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id))

const CopyGradeModal = (props) => {
  const [loader, setLoader] = useState(false)
  const currentId = String(props.gradeId)
  const validationSchema = Yup.object().shape({
    grade: Yup.string()
      .required('Select a comp to copy from')
      .notOneOf(['0'], 'Select a comp to copy from'),
    gradeIds: Yup.array().min(1, 'Select at least one comp to copy to'),
  })
  const formik = useFormik({
    // The comp being edited is the usual target, so it starts in Copy To.
    initialValues: {
      grade: '',
      gradeIds: [currentId],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      setLoader(true)
      GradeService.copyGrade({ grade: data.grade, gradeIds: data.gradeIds })
        .then((res) => {
          if (res.status === 200) {
            props.setCopyVisible(false)
            // Reload the open table only when it was one of the comps overwritten.
            if (data.gradeIds.includes(currentId)) {
              props.toggleDetails(props.gradeId)
            }
            ToastComponent(res.message, 'success')
            actions.resetForm()
          } else {
            ToastComponent(res.message || 'Something went wrong.', 'error')
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  // The source comp can't also be a target.
  const { grade, gradeIds } = formik.values
  useEffect(() => {
    if (grade && gradeIds.includes(grade)) {
      formik.setFieldValue(
        'gradeIds',
        gradeIds.filter((id) => id !== grade),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade])

  // CMultiSelect reads its selection from the options, and fires onChange whenever they
  // change. Rebuilding them on every pick would loop, so they only follow the source comp
  // and pick up the current selection through a ref.
  const gradeIdsRef = useRef(gradeIds)
  gradeIdsRef.current = gradeIds
  const toOptions = useMemo(
    () =>
      (props.gradeList || [])
        .filter((item) => String(item.id) !== grade)
        .map((item) => ({
          value: String(item.id),
          text: item.grade,
          selected: gradeIdsRef.current.includes(String(item.id)),
        })),
    // copyVisible re-seeds the selection after a reset, when the modal opens again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.gradeList, grade, props.copyVisible],
  )

  const close = () => {
    props.setCopyVisible(false)
    formik.resetForm()
  }

  return (
    <>
      <CModal visible={props.copyVisible} onClose={close}>
        <CForm className="" onSubmit={formik.handleSubmit}>
          <CModalHeader>
            <CModalTitle>Copy Point System</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <CRow className="mb-3">
              <CFormLabel htmlFor="grade" className="col-sm-4 col-form-label">
                Copy From:
              </CFormLabel>
              <div className="col-sm-8">
                <CFormSelect
                  aria-label="Copy From"
                  name="grade"
                  id="grade"
                  invalid={!!(formik.errors.grade && formik.touched.grade)}
                  onChange={formik.handleChange}
                  value={formik.values.grade}
                >
                  <option value="0">Select Comp</option>
                  {props.gradeList &&
                    props.gradeList
                      .filter((item) => String(item.id) !== currentId)
                      .map((item) => (
                        <option value={String(item.id)} key={item.id}>
                          {item.grade}
                        </option>
                      ))}
                </CFormSelect>
                {formik.errors.grade && formik.touched.grade && (
                  <CFormFeedback invalid>{formik.errors.grade}</CFormFeedback>
                )}
              </div>
            </CRow>
            <CRow className="mb-3">
              <CFormLabel className="col-sm-4 col-form-label">Copy To:</CFormLabel>
              <div className="col-sm-8">
                <CMultiSelect
                  options={toOptions}
                  selectionType="tags"
                  placeholder="Select comps..."
                  searchNoResultsLabel="No comps found"
                  onChange={(selected) => {
                    const ids = Array.isArray(selected) ? selected.map((o) => String(o.value)) : []
                    if (!sameIds(ids, gradeIdsRef.current)) {
                      formik.setFieldValue('gradeIds', ids)
                      formik.setFieldTouched('gradeIds', true, false)
                    }
                  }}
                />
                {formik.errors.gradeIds && (formik.touched.gradeIds || formik.submitCount > 0) && (
                  <div className="invalid-feedback d-block">{formik.errors.gradeIds}</div>
                )}
                <div className="form-text">
                  The point system of every comp selected here will be replaced.
                </div>
              </div>
            </CRow>
          </CModalBody>
          <CModalFooter>
            <CLoadingButton type="submit" color="success" variant="outline" loading={loader}>
              Apply
            </CLoadingButton>
            <CButton color="danger" onClick={close}>
              Close
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>
    </>
  )
}

export default CopyGradeModal
